import { HistoryIcon } from 'lucide-react';

import { usePendingDisplay } from '@/shared/hooks/usePendingDisplay';
import { FilePaneSkeleton } from './FilePaneSkeleton';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/shared/components/EmptyState';
import { useWorkspaceFileHistory } from '../hooks/useWorkspaceFileHistory';
import { groupByDay } from '../format';
import { mergeUnattributedRepeats } from '../historyMerge';
import { HistoryEntry } from './HistoryEntry';

/**
 * A file's change log, shown in place of its content rather than beside it.
 *
 * It used to be a fixed 16rem rail mounted the moment a file was selected —
 * about a fifth of the content area, permanently, for something read
 * occasionally, and narrow enough that an AI actor's workflow/step/task wrapped
 * onto three lines. The question it was usually opened for ("who changed this
 * last?") is answered in the preview header now; this is the whole story, and
 * it gets the whole width.
 *
 * Loads independently of the preview's own success or failure — a file whose
 * content failed to load can still show its history, and vice versa.
 */
export function HistoryPane({
  workspaceId,
  path,
}: {
  workspaceId: string;
  path: string;
}) {
  const {
    items,
    noHistory,
    isPending,
    loadFailed,
    hasOlder,
    isLoadingOlder,
    loadOlder,
    refetch,
  } = useWorkspaceFileHistory(workspaceId, path);
  const showPlaceholder = usePendingDisplay(isPending);

  if (isPending) {
    return showPlaceholder ? (
      <FilePaneSkeleton label="Loading history" rows={5} />
    ) : null;
  }

  // Checked before the failure branch: "nothing recorded" and "could not be
  // read" look identical in the data (both leave the page unset) but mean
  // opposite things to the reader.
  if (noHistory) {
    return <NoHistory />;
  }

  if (loadFailed) {
    return (
      <EmptyState
        className="m-3"
        icon={HistoryIcon}
        description="This file's history could not be loaded."
        action={
          <Button variant="outline" size="sm" onClick={() => void refetch()}>
            Try again
          </Button>
        }
      />
    );
  }

  if (items.length === 0) {
    return <NoHistory />;
  }

  return (
    <div className="min-h-0 flex-1 overflow-auto">
      {groupByDay(mergeUnattributedRepeats(items)).map((group) => (
        <section key={group.day}>
          {/* The day stays put while its own changes scroll past it, so the
              entry being read never loses the date it belongs to. */}
          <h3 className="bg-background text-muted-foreground border-border sticky top-0 z-10 border-b px-3 py-1.5 text-xs font-medium">
            {group.day}
          </h3>
          <ul>
            {group.events.map((event) => (
              <HistoryEntry key={event.id} event={event} />
            ))}
          </ul>
        </section>
      ))}

      {hasOlder && (
        <div className="flex justify-center p-3">
          {/* Appends rather than paging: Newer/Older replaced the list on every
              click, so reading further back cost you your place in what you
              had already read. */}
          <Button
            variant="outline"
            size="sm"
            disabled={isLoadingOlder}
            onClick={() => void loadOlder()}
          >
            {isLoadingOlder ? 'Loading…' : 'Load older'}
          </Button>
        </div>
      )}
    </div>
  );
}

/** One wording for "there is nothing to show", however the pane got there. */
function NoHistory() {
  return (
    <EmptyState
      className="m-3"
      icon={HistoryIcon}
      description="No history yet."
    />
  );
}
